<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div>
          <h3 class="text-2xl font-bold text-gray-800">
            <i class="fas fa-file-alt mr-2"></i>Détails du Log
          </h3>
          <p class="text-sm text-gray-500 mt-1">ID: #{{ log.id }}</p>
        </div>
        <button @click="close" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Statut général -->
        <div class="detail-section">
          <div class="status-banner" :class="log.status">
            <i :class="statusIcon"></i>
            <div>
              <p class="text-lg font-semibold">
                {{ log.status === 'success' ? 'Requête Réussie' : 'Requête Échouée' }}
              </p>
              <p class="text-sm opacity-90">
                Code HTTP: {{ log.status_code }}
              </p>
            </div>
          </div>
        </div>

        <!-- Informations principales -->
        <div class="detail-section">
          <h4 class="section-title">
            <i class="fas fa-info-circle mr-2"></i>Informations Générales
          </h4>
          <div class="detail-grid">
            <div class="detail-item">
              <label>Endpoint</label>
              <div class="detail-value">
                <code class="endpoint-code">{{ log.endpoint }}</code>
              </div>
            </div>

            <div class="detail-item">
              <label>Date et Heure</label>
              <div class="detail-value">
                <i class="fas fa-calendar mr-2 text-gray-400"></i>
                {{ formatDateTime(log.created_at) }}
              </div>
            </div>

            <div class="detail-item">
              <label>Utilisateur</label>
              <div class="detail-value">
                <i class="fas fa-user-circle mr-2 text-gray-400"></i>
                {{ log.user_id || 'Non spécifié' }}
              </div>
            </div>

            <div class="detail-item">
              <label>Temps de Réponse</label>
              <div class="detail-value">
                <i class="fas fa-clock mr-2 text-gray-400"></i>
                <span :class="getResponseTimeClass(log.response_time_ms)">
                  {{ log.response_time_ms || 0 }} ms
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails techniques -->
        <div class="detail-section">
          <h4 class="section-title">
            <i class="fas fa-code mr-2"></i>Détails Techniques
          </h4>
          <div class="technical-details">
            <div class="code-block">
              <pre>{{ log.details || 'Aucun détail disponible' }}</pre>
            </div>
          </div>
        </div>

        <!-- Métadonnées supplémentaires -->
        <div class="detail-section">
          <h4 class="section-title">
            <i class="fas fa-tags mr-2"></i>Métadonnées
          </h4>
          <div class="metadata-grid">
            <div class="metadata-item">
              <span class="metadata-label">ID</span>
              <span class="metadata-value">{{ log.id }}</span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Statut</span>
              <span :class="['metadata-badge', log.status]">
                {{ log.status }}
              </span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Code HTTP</span>
              <span :class="['metadata-badge', getHttpCodeClass(log.status_code)]">
                {{ log.status_code }}
              </span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Temps (ms)</span>
              <span class="metadata-value">{{ log.response_time_ms || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="copyToClipboard" class="btn btn-secondary">
          <i class="fas fa-copy mr-2"></i>Copier les détails
        </button>
        <button @click="close" class="btn btn-primary">
          <i class="fas fa-check mr-2"></i>Fermer
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogDetailsModal',
  props: {
    log: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  computed: {
    statusIcon() {
      return this.log.status === 'success' 
        ? 'fas fa-check-circle' 
        : 'fas fa-times-circle';
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    formatDateTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    getResponseTimeClass(time) {
      if (time < 100) return 'text-green-600 font-semibold';
      if (time < 500) return 'text-yellow-600 font-semibold';
      return 'text-red-600 font-semibold';
    },
    getHttpCodeClass(code) {
      if (code >= 200 && code < 300) return 'success';
      if (code >= 400 && code < 500) return 'client-error';
      if (code >= 500) return 'server-error';
      return '';
    },
    copyToClipboard() {
      const details = JSON.stringify(this.log, null, 2);
      navigator.clipboard.writeText(details).then(() => {
        alert('Détails copiés dans le presse-papiers !');
      }).catch(err => {
        console.error('Erreur lors de la copie:', err);
      });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: #f3f4f6;
  color: #dc2626;
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.detail-section {
  margin-bottom: 2rem;
}

.status-banner {
  padding: 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2rem;
}

.status-banner.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.status-banner.failure {
  background-color: #fee2e2;
  color: #dc2626;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.detail-item label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.detail-value {
  font-size: 1rem;
  color: #111827;
  display: flex;
  align-items: center;
}

.endpoint-code {
  background-color: #f3f4f6;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #374151;
  display: inline-block;
}

.technical-details {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
}

.code-block {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
}

.code-block pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.metadata-item {
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metadata-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metadata-value {
  font-size: 1.125rem;
  color: #111827;
  font-weight: 600;
}

.metadata-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
}

.metadata-badge.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.metadata-badge.failure {
  background-color: #fee2e2;
  color: #dc2626;
}

.metadata-badge.client-error {
  background-color: #fef3c7;
  color: #f59e0b;
}

.metadata-badge.server-error {
  background-color: #fee2e2;
  color: #dc2626;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
}

.btn-primary {
  background-color: #16a34a;
  color: white;
}

.btn-primary:hover {
  background-color: #15803d;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(22, 163, 74, 0.3);
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
}
</style>

